/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import type { RootState } from "src/reducers";
import { GrClose } from "react-icons/gr";
import axiosWrapper from "@AxiosWrapper";
import { portalMeasurementUnitsUrl } from "@Services/dataManager";
import type { GetMeasurementUnitsResponse } from "@ServerTypes/units/units.schema";
import type { ChangeDisplayUnitRequest } from "@ServerTypes/estimates/estimates.schema";
import { getEstimateData } from "@Actions/EstimateList";
import { changeEstimateUnits, isEstimateRequiredPermission } from "@Actions/estimation";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { useParams } from "react-router-dom";
import { folderPermissions } from "@Helpers/constants";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type MeasurementUnitItem = NonNullable<GetMeasurementUnitsResponse["result"]>[number];

/**
 * Mirrors the optional half of `changeDisplayUnitRequest`: the four `*DisplayUnit` fields are
 * MeasurementUnits `_id`s (`objectIdString` there — the service feeds each to `new ObjectId()`).
 */
type UnitChangePayload = {
  column?: string;
  typeId?: string;
  changeAllMeasurements?: boolean;
  linearDisplayUnit?: ObjectIdString;
  areaDisplayUnit?: ObjectIdString;
  volumeDisplayUnit?: ObjectIdString;
  countDisplayUnit?: ObjectIdString;
};

type MeasurementUnitsByName = {
  [name: string]: MeasurementUnitItem[] | undefined;
};

type DisplayUnitsProps = {
  estimateId: ObjectIdString;
};

function DisplayUnits({ estimateId }: DisplayUnitsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const { RESOURCE_MODIFY } = folderPermissions();
  // `estimateDataSchema` declares all four `*DisplayUnit` fields optional, and the slice starts out
  // as `{}` until getEstimateData resolves, so every read below has to tolerate a missing object.
  const estimateData = useSelector((state: RootState) => state.estimateData);
  const { linearDisplayUnit, areaDisplayUnit, volumeDisplayUnit, countDisplayUnit } = estimateData;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [unitChangeDetails, setUnitChangeDetails] = useState<UnitChangePayload>({});
  const [unitChangeOption, setUnitChangeOption] = useState(false);
  const [measurementUnitsOptions, setMeasurementUnitsOptions] = useState<MeasurementUnitsByName>({});

  const unitTypesList = [
    {
      title: "LINEAR",
      value: "linear",
      name: t("Linear"),
      unitId: linearDisplayUnit?.id,
      unitPayloadName: "linearDisplayUnit",
    },
    {
      title: "AREA",
      value: "area",
      name: t("Area"),
      unitId: areaDisplayUnit?.id,
      unitPayloadName: "areaDisplayUnit",
    },
    {
      title: "VOLUME",
      value: "volume",
      name: t("Volume"),
      unitId: volumeDisplayUnit?.id,
      unitPayloadName: "volumeDisplayUnit",
    },
    {
      title: "COUNT",
      value: "count",
      name: t("Count"),
      unitId: countDisplayUnit?.id,
      unitPayloadName: "countDisplayUnit",
    },
  ];

  useEffect(() => {
    const fetchMeasurementUnits = async () => {
      const unitOptions: MeasurementUnitsByName = {};
      for (const unit of unitTypesList) {
        const response = await axiosWrapper.post<GetMeasurementUnitsResponse>(portalMeasurementUnitsUrl, {
          value: { params: { type: unit.name } },
        });
        if (response.data.result) {
          unitOptions[unit.name] = response.data.result;
        }
      }
      setMeasurementUnitsOptions(unitOptions);
    };
    fetchMeasurementUnits();
  }, []);

  // const beginUnitChange = (e) => {
  //   const { name, value, itemData } = e.target;
  //   const { unitPayloadName } = unitTypesList.find((record: any) => record.name === name);
  //   setUnitChangeDetails({
  //     [unitPayloadName]: value,
  //     column: unitPayloadName,
  //     typeId: itemData.typeId,
  //   });
  //   setShowConfirmModal(true);
  // };

  const submitModal = async () => {
    const data: ChangeDisplayUnitRequest = {
      ...unitChangeDetails,
      changeAllMeasurements: unitChangeOption,
      estimateId,
    };
    const response = await changeEstimateUnits(data);
    if (response.data.status) dispatch(getEstimateData({ estimateId }));
    closeModal();
  };

  const closeModal = () => {
    setShowConfirmModal(false);
    setUnitChangeDetails({});
    setUnitChangeOption(false);
  };

  const confirmationModal = () => (
    <div className="fixed top-0 start-0 w-screen h-screen bg-[var(--background-scrim)] z-[12]">
      <div className="fixed start-[30%] top-[20%] w-[31.25rem] bg-white z-[3] shadow-[0.25rem_0.5rem_1.25rem_0_rgb(0_0_0_/_30%)]">
        <div className="flex flex-row justify-between items-center pt-10 pb-4 px-10 [&_span]:text-2xl">
          <span>{t("Update Default Unit")}</span>
          <GrClose size={20} className="cursor-pointer" onClick={closeModal} />
        </div>
        <div className="flex flex-col text-sm px-10 pb-5">
          <span className="mb-5">
            {t("How would you like to update the display units of the measurements of this estimate?")}
          </span>
          <RadioButton
            label="Override the display units of all measurements"
            onClick={() => setUnitChangeOption(true)}
            checked={unitChangeOption}
          />
          <RadioButton
            label="Only override the display units of the measurements which match the current default display unit"
            onClick={() => setUnitChangeOption(false)}
            checked={!unitChangeOption}
          />
        </div>
        <div className="px-10 pb-10">
          <ButtonComponent buttonType="borderButton" onClick={closeModal}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent buttonType="gradientButton" onClick={submitModal}>
            {t("Confirm")}
          </ButtonComponent>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col justify-between">
      {showConfirmModal && confirmationModal()}
      <div className="w-[31.25rem]">
        {unitTypesList.map((unit, index) => (
          <div key={index} className="mb-12 flex flex-row justify-between items-center">
            <p className="text-sm font-semibold">{unit.name}</p>
            <AutoComplete
              disableClearable
              options={measurementUnitsOptions[unit.name] || []}
              valueField="_id"
              getOptionLabel={(option) => option.name || ""}
              value={unit.unitId}
              disabled={!!params.versionId || !isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id)}
              onChange={async (_event, newValue) => {
                if (!newValue._id) return;
                const { unitPayloadName } = unitTypesList.find((record) => record.name === unit.name) ?? {};
                if (!unitPayloadName) {
                  return;
                }
                const data: ChangeDisplayUnitRequest = {
                  [unitPayloadName]: newValue._id,
                  column: unitPayloadName,
                  changeAllMeasurements: true,
                  estimateId,
                };
                const response = await changeEstimateUnits(data);
                if (response.data.status) dispatch(getEstimateData({ estimateId }));
              }}
              sx={{ width: "18.75rem" }}
              renderInput={(params) => <TextFieldComponent {...params} />}
            />
          </div>
        ))}
      </div>

      <p className="text-xs">
        {t("Note")} -{" "}
        {t(
          "These units are display units for measurements. Quantity calculations will be based on the units of the takeoffs in which these measurements are added",
        )}
        .
      </p>
    </div>
  );
}

export default DisplayUnits;
